'use client';

import { useMicrocopy as useMicrocopyContext } from '@contexts/MicrocopyContext';
import { useMicrocopy } from '@lib/cms/microcopy/client';
import { microcopySetToResourceBundles } from '@lib/coveo/microcopySetToResourceBundles';
import { ExposureEvent } from '@lib/flags/amplitude/ExposureEvent';
import { useFlags } from '@lib/flags/amplitude/client';
import {
  FEATURE_FLAG_ON,
  FLAG_TREATMENT,
  Flags,
} from '@lib/flags/flagConstants';
import { SaleType } from '@lib/types/Sale';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { formatCategorySlug } from '@lib/util/category';
import c from 'classnames';
import Image from 'next/image';
import {
  Badge,
  Link,
  Stars,
  Tooltip,
  TooltipArrow,
  useIsTextTruncated,
  useTooltip,
} from 'ui-components';
import {
  DEFAULT_LOCALE,
  formatDisplayableCurrency,
  interpolateStringWithData,
} from 'utils';
import { ProductCardProps } from './ProductCard.types';
import { ProductCardBookmarkButton } from './ProductCardBookmarkButton';
import { ProductCardSaleCountdown } from './ProductCardSaleCountdown';
import { ProductCardSaleOverlay } from './ProductCardSaleOverlay';
import { useSaleState } from './useIsActiveSale';

const CATEGORY_ICONS: Record<string, string> = {
  '3d': 'Cube',
  '2d': 'ImageSquare',
  audio: 'Headphones',
  tools: 'Wrench',
  vfx: 'MagicWand',
  templates: 'CopySimple',
  ai: 'Circuitry',
  'add-ons': 'Plug',
  essentials: 'PuzzlePiece',
  industry: 'GearSix',
  sdk: 'Code',
  decentralization: 'ShareNetwork',
  animation: 'FilmStrip',
};

export const ProductCard = ({
  name,
  productId,
  imageUrl,
  publisher,
  rating = 0,
  ratingCount = 0,
  price,
  sale,
  category,
  isVerifiedSolution,
  isNewRelease,
  isUnityAwardsWinner,
  entitlement,
  locale: _locale = DEFAULT_LOCALE,
  showFavorite,
  isFavorite,
  extraEventHandlers = {},
  className = '',
}: ProductCardProps) => {
  const { onFavorite, ...linkEventHandlers } = extraEventHandlers;
  const microcopy = useMicrocopy('product-card', {
    'product-card.purchased': 'Purchased',
    'product-card.purchased-seats': 'Purchased Seat(s)',
    'product-card.owned': 'You own this asset',
    'product-card.verified-solution-tooltip': 'Verified by Unity',
    'product-card.verified-solution-badge': 'Verified Solution',
    'product-card.new-release-badge': 'New Release',
    'product-card.unity-awards-winner-badge': 'Unity Awards Winner',
    'product-card.free': 'Free',
    'product-card.not-enough-ratings': 'Not enough ratings',
    'product-card.star-rating-label': 'Rating: {{rating}} out of 5 stars',
    'product-card.add-to-favorites': 'Add to favorites',
    'product-card.remove-from-favorites': 'Remove from favorites',
  });

  const { searchValuesMicrocopy } =
    useMicrocopyContext<{
      searchValuesMicrocopy?: MicrocopyItem[];
    }>() ?? {};

  const getCategoryDisplayName = (categoryKey?: string): string | undefined => {
    if (!categoryKey) return undefined;

    // Try microcopy lookup first
    if (searchValuesMicrocopy) {
      const categoryResourceBundle = microcopySetToResourceBundles(
        searchValuesMicrocopy
      ).ec_category;
      const microcopyValue =
        categoryResourceBundle?.[categoryKey.toLowerCase()];
      if (microcopyValue) {
        return microcopyValue;
      }
    }

    // Fallback: format the slug as display name
    return formatCategorySlug(categoryKey);
  };

  const { active, upcoming } = useSaleState(sale);

  const {
    context,
    isOpen,
    refs,
    floatingStyles,
    getReferenceProps,
    getFloatingProps,
    arrowRef,
  } = useTooltip({
    offset: 0,
    arrow: true,
  });

  const isTextTruncated = useIsTextTruncated(
    refs.reference as React.RefObject<HTMLDivElement>
  );

  const { flags } = useFlags();
  const openInNewTabFlag = flags[Flags.OPEN_RESULTS_IN_NEW_TAB];
  const shouldOpenProductInNewTab = openInNewTabFlag?.value === FEATURE_FLAG_ON;

  // A/B test (as-countdown-timer): treatment shows the redesigned inline
  // countdown pill + thumbnail New Release badge; control keeps the thumbnail
  // countdown overlay + tag-row New Release badge.
  const isCountdownRedesign =
    flags[Flags.COUNTDOWN_TIMER]?.value === FLAG_TREATMENT;

  const isOwned = !!entitlement;
  // CPS-7528 (both arms): only flash deals and new-release discounts show a
  // countdown. Standard sales frequently have their end dates reset, so their
  // countdown is inaccurate and unhelpful (the same date on dozens of cards).
  // Note we gate the countdown only — `active` still drives sale pricing, so
  // standard sales keep their discounted price.
  const isCountdownSaleType =
    sale?.type === SaleType.FlashDealTimeBased ||
    sale?.type === SaleType.NewReleaseDiscount;
  // The `isNewRelease` flag (recently published) misses products whose launch
  // discount is still running — the surfaces set it independently of the sale
  // (e.g. ASv2 `new` vs a 90-day new-release discount) — so an active
  // new-release discount also earns the badge (CPS-7269). Gated on `active`:
  // when the discount ends in an open tab, useIsActiveSale flips `active` and
  // the badge retires with the rest of the sale UI, matching what the next
  // server render would show (expired sales aren't returned).
  const isNewReleaseCard =
    isNewRelease || (active && sale?.type === SaleType.NewReleaseDiscount);
  // Flag-independent: the cards where the two arms render differently — a
  // countdown-eligible sale (inline pill vs thumbnail overlay) or a new
  // release (badge placement). Drives the A/B exposure so it fires
  // symmetrically in both arms; it must not depend on the arm itself.
  const isCountdownExperimentSurface =
    ((active || upcoming) && !!sale && !isOwned && isCountdownSaleType) ||
    isNewReleaseCard;
  const showSaleCountdown =
    (active || upcoming) && sale && !isOwned && isCountdownSaleType;
  // Single source for the -X% badge condition: guards both the badge JSX and
  // the pill's attachedToDiscount so the fused geometry can't desync from the
  // badge's presence.
  const hasDiscountBadge =
    active && price.currentPrice !== 0 && price.discountPercent !== undefined;

  const currentPriceFormatted = formatDisplayableCurrency(
    active ? price.currentPrice : price.originalPrice,
    DEFAULT_LOCALE,
    price.currency
  );
  const originalPriceFormatted = formatDisplayableCurrency(
    price.originalPrice,
    DEFAULT_LOCALE,
    price.currency
  );

  const roundedRating = Math.round(rating * 10) / 10;
  const hasRating = ratingCount > 0 && rating > 0;

  // The countdown redesign moves this badge onto the thumbnail; control keeps
  // it in the tag row. Same badge either way, so define it once.
  const newReleaseBadge = (
    <Badge
      text={microcopy['product-card.new-release-badge']}
      className="rounded py-0 leading-5 tracking-tight text-grey-700"
      data-test="product-card-tag-new-release"
    />
  );

  return (
    <article
      data-test={`search-results-product-card-${productId}`}
      className={c(
        'group relative flex w-full flex-col rounded-lg bg-white',
        className
      )}
    >
      {/* Fire the A/B exposure whenever this card is part of the experiment
          surface (flag-independent), not gated on the arm-dependent
          `showSaleCountdown` — otherwise treatment sessions that only surface
          standard sales would be under-counted vs. control (CPS-7528). */}
      {isCountdownExperimentSurface && (
        <ExposureEvent flagName={Flags.COUNTDOWN_TIMER} />
      )}
      <div className="relative aspect-3/2 overflow-hidden rounded-t-lg">
        <Image
          fill
          sizes="50vw, (min-width: 768px) 25vw, (min-width: 1024px) 33vw"
          alt=""
          aria-hidden={true}
          className={c('object-cover', isOwned && 'opacity-40')}
          src={imageUrl}
        />
        {/* left-3 aligns the badge with the tag-row badges below (the card
            body's px-3), not the Figma-frame-relative left-2. Owned cards dim
            the badge like the thumbnail: it floats above the image, so it
            doesn't inherit the dim the control's tag-row badge gets from the
            card body. */}
        {isCountdownRedesign && isNewReleaseCard && (
          <div
            className={c('absolute left-3 top-2 z-10', isOwned && 'opacity-40')}
          >
            {newReleaseBadge}
          </div>
        )}
        {!isCountdownRedesign && showSaleCountdown && (
          <ProductCardSaleOverlay sale={sale} isActive={active} />
        )}
      </div>

      <div className="flex h-40 flex-col rounded-b-lg border-x border-b border-grey-200 px-3 pb-3">
        <div
          className={c(
            'flex h-full flex-col gap-3',
            isOwned && 'opacity-40 group-hover:opacity-100'
          )}
        >
          <div className="mt-3 flex h-5 flex-nowrap gap-1 overflow-hidden">
            {category && (
              <Badge
                text={getCategoryDisplayName(category) ?? category}
                iconName={
                  CATEGORY_ICONS[category.toLowerCase()] ?? 'PuzzlePiece'
                }
                className="rounded py-0 leading-5 tracking-tight text-grey-700"
                data-test={`product-card-tag-category-${category.toLowerCase()}`}
              />
            )}
            {isVerifiedSolution && (
              <Badge
                text={microcopy['product-card.verified-solution-badge']}
                tooltip={microcopy['product-card.verified-solution-tooltip']}
                className="relative z-10 rounded py-0 leading-5 tracking-tight text-grey-700"
                tooltipClassName="z-50"
                data-test="product-card-tag-verified-solution"
              />
            )}
            {!isCountdownRedesign && isNewReleaseCard && newReleaseBadge}
            {isUnityAwardsWinner && (
              <Badge
                text={microcopy['product-card.unity-awards-winner-badge']}
                iconName="Star"
                className="rounded py-0 leading-5 tracking-tight text-grey-700"
                data-test="product-card-tag-unity-awards"
              />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Link
              href={`/packages/package/${productId}`}
              data-test="product-card-name"
              target={shouldOpenProductInNewTab ? '_blank' : undefined}
              className="focus-outline rounded after:absolute after:inset-0"
              {...linkEventHandlers}
            >
              <span
                className="relative z-10 line-clamp-1 h-5 text-sm leading-5 tracking-tight text-grey-900"
                ref={refs.setReference}
                {...getReferenceProps()}
              >
                {name}
              </span>
            </Link>

            {isOpen && isTextTruncated && (
              <div
                ref={refs.setFloating}
                style={floatingStyles}
                {...getFloatingProps()}
              >
                <TooltipArrow arrowRef={arrowRef} context={context} />
                <Tooltip label={name} className="max-w-56" />
              </div>
            )}

            {hasRating ? (
              <div className="flex h-5 items-center gap-1 text-sm leading-5 text-grey-900">
                <Stars
                  value={roundedRating}
                  width={16}
                  ariaLabel={interpolateStringWithData(
                    microcopy['product-card.star-rating-label'],
                    { rating: roundedRating.toString() }
                  )}
                />
                <span className="text-grey-900" data-test="product-rating">
                  {roundedRating}
                </span>
                <span
                  className="text-grey-600"
                  data-test="product-rating-count"
                >
                  ({ratingCount})
                </span>
              </div>
            ) : (
              <div className="h-5 text-sm leading-5 text-grey-600">
                {microcopy['product-card.not-enough-ratings']}
              </div>
            )}

            <div className="h-5">
              {publisher && (
                <Link
                  data-test="product-card-publisher"
                  target={shouldOpenProductInNewTab ? '_blank' : undefined}
                  className="focus-outline relative z-10 block truncate rounded text-sm leading-5 text-grey-600 hover:text-blue-hover"
                  href={`/publishers/${publisher.id}`}
                >
                  {publisher.name}
                </Link>
              )}
            </div>

            {isOwned ? (
              <div
                className="h-5 text-sm leading-5 text-grey-900"
                data-test="product-card-purchased-label"
              >
                <span>{microcopy['product-card.owned']}</span>
              </div>
            ) : (
              // Rows that render the treatment countdown pill differ from
              // production in two ways, both per the Figma sale-row variant
              // and both gated on the same condition so every other row stays
              // byte-identical to control (standard sales are outside the
              // exposure surface):
              // - gap-1: the sale row is a uniform 4px-gap group, visually
              //   connecting the price, discount and countdown chips (and
              //   freeing 12px for the fit check below). The pill's -ml-2 in
              //   ProductCardSaleCountdown hard-codes this 4px gap — keep in
              //   sync.
              // - fit-based hiding: the row wraps but is clamped to one line
              //   (h-5 + overflow-hidden + content-start), and the pill never
              //   breaks internally (whitespace-nowrap) — a pill that doesn't
              //   fit wraps as a unit onto the clipped second line, so it
              //   renders complete or not at all, never truncated.
              <div
                className={c(
                  'flex h-5 items-center',
                  isCountdownRedesign && showSaleCountdown
                    ? 'flex-wrap content-start gap-1 overflow-hidden'
                    : 'gap-2'
                )}
                data-test="search-results-price"
              >
                {price.currentPrice === 0 ? (
                  <span
                    data-test="product-card-current-price"
                    className="text-sm leading-5 text-grey-900"
                  >
                    {microcopy['product-card.free']}
                  </span>
                ) : (
                  <>
                    <span
                      className="text-sm leading-5 text-grey-900"
                      data-test="product-card-current-price"
                    >
                      {currentPriceFormatted}
                    </span>
                    {hasDiscountBadge && (
                      <>
                        {/* Figma's sale row specifies #A6A6A6 (grey-400) for
                            the struck price, but that is ~2.4:1 on white —
                            far below WCAG AA's 4.5:1 — so both arms keep the
                            production grey-600 (5.7:1) pending a design
                            revisit */}
                        <span
                          className="text-sm leading-5 text-grey-600 line-through"
                          data-test="product-card-original-price"
                        >
                          {originalPriceFormatted}
                        </span>
                        <span
                          className="rounded bg-red-200 px-1 text-xs leading-5 text-red-600"
                          data-test="product-card-discount"
                        >
                          -{price.discountPercent}%
                        </span>
                      </>
                    )}
                  </>
                )}
                {/* Rendered outside the free/non-free ternary so the countdown
                    pill appears for any qualifying sale (incl. a 100%-off flash
                    deal that drops the price to 0), matching the control
                    overlay which is not price-gated. */}
                {isCountdownRedesign && showSaleCountdown && (
                  <ProductCardSaleCountdown
                    sale={sale}
                    isActive={active}
                    // fuses the pill onto the -X% badge when one renders
                    // before it (the Figma sale badge is one container)
                    attachedToDiscount={hasDiscountBadge}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {showFavorite && isFavorite != null && (
        <ProductCardBookmarkButton
          name={name}
          isFavorite={isFavorite}
          addToFavoritesLabel={microcopy['product-card.add-to-favorites']}
          removeFromFavoritesLabel={
            microcopy['product-card.remove-from-favorites']
          }
          onFavorite={onFavorite}
        />
      )}
    </article>
  );
};
